import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import { connect } from 'redaction'
import actions from 'redux/actions'
import ValueLink from 'sb-valuelink'
import { debounce } from 'lodash'
import { compose, constants, isNotEmpty, mixpanel, scrollToElement, snowplow } from 'common/helpers'

import injectGlobals from 'decorators/injectGlobals'

import ComboBox, { InputWrapper } from 'components/_new/ada/ComboBox'

import SearchSuggestions from './SearchSuggestions/SearchSuggestions'
import SearchResult from './SearchResult/SearchResult'
import SearchInput from './SearchInput/SearchInput'

import s from './HeaderSearchInput.scss'


// TODO: rewrite and refactor header search input totally to prevent it from rerender
class HeaderSearchInput extends Component {

  static propTypes = {
    isMobile: PropTypes.bool,
    isDesktop: PropTypes.bool,
    isNewMobileInitialViewEnabled: PropTypes.bool,
    result: PropTypes.shape({
      isBestsellers: PropTypes.bool,
      isExactBrandMatch: PropTypes.bool,
      items: PropTypes.array,
    }),
    areAnyModalsOpen: PropTypes.bool,
  }

  constructor({ searchValue }) {
    super()

    this.state = {
      searchValue,
      isFetching: false,
    }
  }

  componentDidMount() {
    this.bindListeners()

    this.searchInput = document.querySelector('[data-id="searchInput"]')
  }

  componentWillUnmount() {
    this.unbindListeners()
  }

  bindListeners = () => {
    document.addEventListener('click', this.close)
  }

  unbindListeners = () => {
    document.removeEventListener('click', this.close)
  }

  sendQueryAnalyticsEvent = () => {
    const { searchValue } = this.state
    const snowplowMetadata = {
      query: searchValue,
    }

    snowplow.track(constants.snowplowEvents.search.query, snowplowMetadata, false)
  }

  /**
   * @param {Object} options
   * @param {string} options.suggestionType
   * @param {number} options.position
   */
  handleSuggestionClick = ({ suggestionType, position }) => {
    const snowplowMetadata = {
      suggestionType,
      position,
    }
    const mixpanelMetadata = {
      placement: `position ${position}`,
    }

    this.close()
    snowplow.track(constants.snowplowEvents.search.suggestionClick, snowplowMetadata)
    this.sendQueryAnalyticsEvent()
  }

  /**
   * @param {Object} options
   * @param {Object} options.item
   * @param {number} options.item.id
   * @param {string} options.item.name
   * @param {boolean} options.item.available
   * @param {number} options.position
   * @return {void}
   */
  handleItemClick = ({ item: { id, name, available }, position }) => {
    const { searchValue } = this.state

    const snowplowEvent = available
      ? constants.snowplowEvents.search.nameClick
      : constants.snowplowEvents.search.nameUnavailableClick

    const snowplowMetadata = {
      query: searchValue,
      productId: id,
      productName: name,
      position,
    }

    this.close()
    snowplow.track(snowplowEvent, snowplowMetadata)
    this.sendQueryAnalyticsEvent()
  }

  handleBrandClick = (brand) => {
    const { searchValue } = this.state

    const snowplowEvent = brand && brand.available
      ? constants.snowplowEvents.search.brandClick
      : constants.snowplowEvents.search.brandUnavailableClick

    const snowplowMetadata = {
      query: searchValue,
      brand: brand.name,
    }

    this.close()
    snowplow.track(snowplowEvent, snowplowMetadata)
    this.sendQueryAnalyticsEvent()
  }

  handleBestsellersLinkClick = () => {
    const { searchValue } = this.state

    const snowplowMetadata = {
      query: searchValue,
    }

    this.close()
    snowplow.track(constants.snowplowEvents.search.bestsellersClick, snowplowMetadata)
    this.sendQueryAnalyticsEvent()
  }

  fetchProduct = debounce((value = '') => {
    const searchValue = value.trim().replace(/\s+/g, ' ')

    actions.models.search.setSearchValue(searchValue)

    if (searchValue && searchValue.length > 2) {
      this.setState({ isFetching: true })

      actions.models.search.fetch(searchValue)
        .finally(() => this.setState({ isFetching: false }))

      return
    }

    actions.models.search.clear()
  }, 400)

  tryDispatchFocusEvent = () => {
    const { areAnyModalsOpen } = this.props

    if (!areAnyModalsOpen) {
      const event = new CustomEvent('touchstart', { bubbles: true, detail: true })

      this.searchInput.dispatchEvent(event)
    }
  }

  addItem = (item) => {
    const { id, name, category, upchargePrice } = item

    snowplow.track(constants.snowplowEvents.addToQueue, {
      placement: 'search bar',
      productId: id,
      productName: name,
      productCategory: category,
      upchargePrice: upchargePrice ? upchargePrice / 100 : null,
    }, false)

    actions.models.queue.addItem(item, { placement: 'search bar' })
      .then(this.tryDispatchFocusEvent)
  }

  handleFocusInput = (isOpen, setIsOpen) => {
    const { isNewMobileInitialViewEnabled } = this.props

    if (!isOpen) {
      setIsOpen(true)

      snowplow.track(constants.snowplowEvents.searchInputClick)
    }

    if (isNewMobileInitialViewEnabled) {
      scrollToElement(this.searchInput, true)
    }
  }

  handleTouchStart = (setIsOpen) => {
    setIsOpen(true)
  }

  clearSearch = () => {
    const { searchValue } = this.state

    const snowplowMetadata = {
      query: searchValue,
    }

    actions.models.search.clear()
    this.valueLinks.searchValue.set('')
    snowplow.track(constants.snowplowEvents.search.cancel, snowplowMetadata, false)
  }

  render() {
    const { isFetching } = this.state
    const {
      result, isMobile, isNewMobileInitialViewEnabled,
      searchValue: savedSearchValue, gender, isDesktop,
    } = this.props
    const { items } = result

    return (
      <ComboBox
        uid="searchInput"
        name="searchInput"
        label="Search"
        onAfterClose={!isMobile ? this.clearSearch : null}
      >
        {
          ({ id, controlsId, inputId, descendantId, activeDescendantId, isOpen, setIsOpen }) => {
            this.valueLinks = ValueLink.all(this, 'searchValue')

            // TODO: refactor whole header search input
            this.close = () => setIsOpen(false)

            const searchValue = this.valueLinks.searchValue.value.trim().replace(/\s+/g, ' ')

            const isInputNotEmpty = Boolean(searchValue)
            const isResultVisible = Boolean(
              isOpen
              && isInputNotEmpty
              && items.length
              && searchValue.length > 2
            )
            const isNewMobileViewVisible  = Boolean(isOpen && isNewMobileInitialViewEnabled)
            const isDesktopViewVisible    = Boolean(isOpen && isDesktop)

            const isEmptySearchSuggestionsVisible = (
              !isInputNotEmpty
              && !isResultVisible
              && (isNewMobileViewVisible || isDesktopViewVisible)
            )

            const rootClassName = cx(s.searchContainer, {
              [s.desktopContainer]: isDesktop,
              [s.isFocused]: isOpen,
            })

            return (
              <div className={rootClassName} data-testid={constants.testIds.layout.search}>
                <InputWrapper
                  id={id}
                  inputId={inputId}
                  controlsId={controlsId}
                  activeDescendantId={activeDescendantId}
                  label="Search"
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
                >
                  <SearchInput
                    isMobile={isMobile}
                    isNewMobileViewVisible={isNewMobileViewVisible}
                    isDesktop={isDesktop}
                    isFocused={isOpen}
                    isFetching={isFetching}
                    valueLink={this.valueLinks.searchValue.onChange(this.fetchProduct)}
                    onFocus={() => this.handleFocusInput(isOpen, setIsOpen)}
                    onTouchStart={() => this.handleTouchStart(setIsOpen)}
                  />
                </InputWrapper>
                {
                  isEmptySearchSuggestionsVisible && (
                    <SearchSuggestions
                      className={s.initialViewResult}
                      isMobile={isMobile}
                      gender={gender}
                      handleSuggestionClick={this.handleSuggestionClick}
                      controlsId={controlsId}
                      descendantId={descendantId}
                    />
                  )
                }
                {
                  isNewMobileViewVisible && !isFetching && (
                    <Fragment>
                      <div className={s.closeArrow} onClick={this.close} />
                      {
                        isInputNotEmpty && (
                          <label
                            htmlFor={inputId}
                            className={s.clearSearchBtn}
                            onMouseEnter={this.unbindListeners}
                            onMouseLeave={this.bindListeners}
                            onClick={this.clearSearch}
                            tabIndex={-1}
                          />
                        )
                      }
                    </Fragment>
                  )
                }
                {
                  isResultVisible && (
                    <SearchResult
                      className={s.result}
                      result={result}
                      gender={gender}
                      isDesktop={isDesktop}
                      searchValue={savedSearchValue}
                      onItemClick={this.handleItemClick}
                      onAddButtonClick={this.addItem}
                      onBrandLinkClick={this.handleBrandClick}
                      onBestsellersLinkClick={this.handleBestsellersLinkClick}
                      bindListeners={this.bindListeners}
                      unbindListeners={this.unbindListeners}
                      controlsId={controlsId}
                      descendantId={descendantId}
                    />
                  )
                }
              </div>
            )
          }
        }
      </ComboBox>
    )
  }
}


export default compose(
  injectGlobals(({ globals }) => ({
    isNewMobileInitialViewEnabled: globals.device.isMobile,
    isDesktop: globals.device.isDesktop,
  })),
  connect({
    result: 'models.search',
    gender: 'models.me.gender',
    searchValue: 'models.search.searchValue',
    areAnyModalsOpen: ({ models: { modals } }) => isNotEmpty(modals),
  }),
)(HeaderSearchInput)
